import { useState } from "react";
import api, { errMsg, fmtDate } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { Send } from "lucide-react";

export function RfqDialog({ req, suppliers, open, onOpenChange, onSent }) {
  const { t } = useI18n();
  const [ids, setIds] = useState([]);
  const [deadline, setDeadline] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const asked = new Set((req.rfq_log || []).map((r) => r.supplier_id));
  const toggle = (id) => setIds((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/requisitions/${req.id}/rfq`, { supplier_ids: ids, deadline, message });
      if (data.sent.length) toast.success(`${t("rfq.sent")}: ${data.sent.map((s) => s.supplier_name).join(", ")}`);
      data.failed.forEach((f) => toast.error(`${f.supplier_name || f.supplier_id}: ${f.error}`));
      if (data.sent.length) { onOpenChange(false); setIds([]); onSent?.(); }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="rfq-dialog">
        <DialogHeader>
          <DialogTitle className="uppercase font-black">{t("rfq.title")} · {req.number}</DialogTitle>
          <DialogDescription className="text-xs">{t("rfq.hint")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1 max-h-56 overflow-y-auto border p-2" data-testid="rfq-supplier-list">
          {suppliers.filter((s) => s.active !== false).map((s) => (
            <label key={s.id} className={`flex items-center gap-2 text-xs p-1 ${s.email ? "cursor-pointer hover:bg-muted" : "opacity-50"}`}>
              <input type="checkbox" disabled={!s.email} checked={ids.includes(s.id)} onChange={() => toggle(s.id)} data-testid={`rfq-supplier-${s.id}`} />
              <span className="font-bold">{s.name}</span>
              <span className="text-muted-foreground truncate">{s.email || t("rfq.noEmail")}</span>
              {asked.has(s.id) && <span className="ml-auto text-[9px] font-bold uppercase text-sky-700 border border-sky-300 bg-sky-50 px-1">{t("rfq.alreadySent")}</span>}
            </label>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1"><Label className="text-[10px] font-bold uppercase tracking-widest">{t("rfq.deadline")}</Label>
            <Input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} className="rounded-none h-8 text-xs" data-testid="rfq-deadline" /></div>
          <div className="space-y-1 col-span-2"><Label className="text-[10px] font-bold uppercase tracking-widest">{t("po.emailMessage")}</Label>
            <Textarea rows={2} value={message} onChange={(e) => setMessage(e.target.value)} className="rounded-none text-xs" data-testid="rfq-message" /></div>
        </div>
        {(req.rfq_log || []).length > 0 && (
          <div className="text-[10px] text-muted-foreground" data-testid="rfq-log">
            {t("rfq.history")}: {req.rfq_log.map((r) => `${r.supplier_name} (${fmtDate(r.sent_at)})`).join(" · ")}
          </div>
        )}
        <DialogFooter>
          <Button onClick={send} disabled={busy || ids.length === 0} className="rounded-none text-xs font-bold" data-testid="rfq-send">
            <Send size={12} className="mr-1" /> {busy ? "…" : `${t("rfq.send")} (${ids.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
